import {
  useEffect,
  useLayoutEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import { BookingApi } from 'shared/api';
import { bookingSocketService } from 'shared/api/booking/model/socket-booking';
import { businessStore } from 'entities/index';
import * as dateFns from 'date-fns';
import { CalendarCardType } from '../types/business-card.type';
import {
  CalendarDayType,
  IEmployeeDaySchedule,
} from 'shared/api/booking/interfaces/calendar-day.type';
import { DATE_FORMAT } from 'shared/consts/date-format';

const heightMultiple = 0.9;
export const microSlotHeightBase = (60 * heightMultiple) / 4;

const isWorkingDay = (employee: IEmployeeDaySchedule): boolean => {
  if (!employee.workDay?.startedAt || !employee.workDay?.finishedAt)
    return false;
  if (employee.workDay.type === 'dayOff') return false;
  return true;
};

export function useDayCalendarModel(
  date: Date,
  pointId: string | null,
  microSlotDuration: number
) {
  const hourSlotHeight = (60 / microSlotDuration) * microSlotHeightBase;
  const [calendarData, setCalendarData] = useState<CalendarDayType>();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [pendingMove, setPendingMove] = useState<{
    type: 'booking' | 'break';
    id: string;
    dateStr: string;
    newStartedAt: string;
    newFinishedAt: string;
    message: string;
  } | null>(null);
  const employeeListRef = useRef<HTMLDivElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);

  const leftBorderRef = useRef<HTMLDivElement>(null);
  const employeeHeaderRef = useRef<HTMLDivElement>(null);
  const timesContainerRef = useRef<HTMLDivElement>(null);
  const calendarScalerRef = useRef<HTMLDivElement>(null);
  const coreEmployeeRef = useRef<HTMLDivElement>(null);

  const toMinutes = (time: string) => {
    const [h, m] = time.split(':').map(Number);
    const startHour = parseInt(minStartTime.split(':')[0]);
    return (h - startHour) * hourSlotHeight + m * (hourSlotHeight / 60);
  };

  const timeToMinutes = (time: string) => {
    const [h, m] = time.split(':').map(Number);
    return h * 60 + m;
  };

  const getTimelinePosition = (date: Date) => {
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const startHour = parseInt(minStartTime.split(':')[0]);
    const hoursFromStart = hours - startHour;
    if (hoursFromStart < 0) return 0;
    return hoursFromStart * hourSlotHeight + minutes * (hourSlotHeight / 60);
  };

  const minStartTime = useMemo(() => {
    if (!calendarData) return '00:00';
    const workDays = calendarData
      .filter(
        (e): e is typeof e & { workDay: NonNullable<typeof e.workDay> } =>
          e.workDay !== undefined && e.workDay?.type !== 'dayOff'
      )
      .map((e) => e.workDay.startedAt)
      .filter((time): time is string => time !== null)
      .sort();

    if (workDays.length === 0) return '00:00';

    const earliestStart = workDays[0];
    const earliestHour = parseInt(earliestStart.split(':')[0]);
    const displayStartHour = Math.max(0, earliestHour - 1);

    return `${String(displayStartHour).padStart(2, '0')}:00`;
  }, [calendarData]);

  const maxEndTime = useMemo(() => {
    if (!calendarData) return '24:00';
    const workDays = calendarData
      .filter(
        (e): e is typeof e & { workDay: NonNullable<typeof e.workDay> } =>
          e.workDay !== undefined && e.workDay?.type !== 'dayOff'
      )
      .map((e) => e.workDay.finishedAt)
      .filter((time): time is string => time !== null)
      .sort()
      .reverse();
    return workDays.length > 0 ? workDays[0] : '24:00';
  }, [calendarData]);

  const dayTimes = useMemo(() => {
    const startHour = parseInt(minStartTime.split(':')[0]);
    const endHour = parseInt(maxEndTime.split(':')[0]);
    const times: string[] = [];
    for (let i = startHour; i <= endHour; i++) {
      times.push(`${String(i).padStart(2, '0')}:00`);
    }
    return times;
  }, [minStartTime, maxEndTime]);

  const totalDayHeight = useMemo(
    () => dayTimes.length * hourSlotHeight,
    [dayTimes, hourSlotHeight]
  );

  const [cardData, setCardData] = useState<{
    id: string;
    type: CalendarCardType;
    employeeId: string;
  } | null>(null);
  const [dragState, setDragState] = useState<{
    item: {
      id: string;
      type: CalendarCardType;
      employeeId: string;
      originalTop: number;
      height: number;
      startedAt: string;
      finishedAt: string;
    };
    startY: number;
    currentDeltaMinutes: number;
    previewTop: number;
  } | null>(null);
  const [isDraggingItem, setIsDraggingItem] = useState(false);
  const [didDrag, setDidDrag] = useState(false);
  const [scale, setScale] = useState(1);
  const scaleRef = useRef(1);
  const gestureState = useRef({
    initialDistance: 0,
    initialScale: 1,
    isPinching: false,
  });
  const [calendarDimensions, setCalendarDimensions] = useState({
    width: 0,
    height: 0,
  });
  const dragTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearDragTimeout = () => {
    if (dragTimeoutRef.current) {
      clearTimeout(dragTimeoutRef.current);
      dragTimeoutRef.current = null;
    }
  };
  const cancelDragListenersRef = useRef<(() => void) | null>(null);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(
    null
  );
  const employeeCardRefs = useRef<Map<string, HTMLDivElement | null>>(
    new Map()
  );
  const [createModalData, setCreateModalData] = useState<{
    startTime: string;
    duration: number;
    employeeId: string;
    date: Date;
  } | null>(null);
  const [selectedBookingData, setSelectedBookingData] = useState<{
    startTime: string;
    employeeId: string;
    date: Date;
  } | null>(null);
  const [selectedBreakData, setSelectedBreakData] = useState<{
    startTime: string;
    employeeId: string;
    date: Date;
  } | null>(null);
  const calendarContainerRef = useRef<HTMLDivElement>(null);
  const calendarDataRef = useRef(calendarData);
  calendarDataRef.current = calendarData;
  const [scrollLeft, setScrollLeft] = useState(0);
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);

  const handleBlockClick = (
    id: string,
    type: CalendarCardType,
    employeeId: string
  ) => {
    setCardData({
      id: id,
      type: type,
      employeeId: employeeId,
    });
  };

  const handleCloseCard = (updateData = false) => {
    if (updateData) {
      getData(date);
    }
    setCardData(null);
  };

  const handleToggleEmployeePopup = (employeeId: string) => {
    setSelectedEmployeeId((prev) => (prev === employeeId ? null : employeeId));
  };

  const handleCreateBooking = (data: {
    startTime: string;
    employeeId: string;
    date: Date;
  }) => {
    setSelectedBookingData(data);
  };

  const handleCreateBreak = (data: {
    startTime: string;
    employeeId: string;
    date: Date;
  }) => {
    setSelectedBreakData(data);
  };

  const handleConfirmMove = async () => {
    if (!pendingMove) return;
    try {
      if (pendingMove.type === 'booking') {
        await BookingApi.updateBooking({
          bookingId: pendingMove.id,
          startAt: (() => {
            const [startHours, startMinutes] =
              pendingMove.newStartedAt.split(':');
            const startDate = new Date(pendingMove.dateStr);
            startDate.setHours(
              parseInt(startHours, 10),
              parseInt(startMinutes, 10),
              0,
              0
            );
            return dateFns.format(startDate, "yyyy-MM-dd'T'HH:mm:ssxxx");
          })(),
          finishAt: (() => {
            const [endHours, endMinutes] = pendingMove.newFinishedAt.split(':');
            const endDate = new Date(pendingMove.dateStr);
            endDate.setHours(
              parseInt(endHours, 10),
              parseInt(endMinutes, 10),
              0,
              0
            );
            return dateFns.format(endDate, "yyyy-MM-dd'T'HH:mm:ssxxx");
          })(),
        });
      } else {
        await BookingApi.updateBreakOverride(pendingMove.id, {
          startedAt: pendingMove.newStartedAt,
          finishedAt: pendingMove.newFinishedAt,
          date: pendingMove.dateStr,
        });
      }
      getData(date);
    } catch (error) {
      console.error('Failed to update:', error);
      setAlertMessage('Не удалось обновить время');
    }
    setPendingMove(null);
  };

  useEffect(() => {
    if (pointId) {
      businessStore.initEmployeesWidgetData(pointId);
    }
  }, [pointId]);

  const getData = async (targetDate: Date) => {
    if (pointId) {
      await BookingApi.getCalendarDay({
        date: targetDate,
        pointId,
      })
        .then((data) => {
          const sortedData = [...data].sort((a, b) => {
            const aWorking = isWorkingDay(a);
            const bWorking = isWorkingDay(b);
            if (aWorking && !bWorking) return -1;
            if (!aWorking && bWorking) return 1;
            return 0;
          });
          setCalendarData(sortedData);
        })
        .catch((err) => {
          console.error('getData error:', err);
        });
    }
  };

  const handleDayUpdate = (data: { type: string; data?: CalendarDayType }) => {
    if (data.type === 'full' && data.data) {
      const sortedData = [...data.data].sort((a, b) => {
        const aWorking = isWorkingDay(a);
        const bWorking = isWorkingDay(b);
        if (aWorking && !bWorking) return -1;
        if (!aWorking && bWorking) return 1;
        return 0;
      });
      setCalendarData(sortedData);
    } else {
      getData(date);
    }
  };

  useEffect(() => {
    getData(date);
  }, [date, pointId]);

  useEffect(() => {
    if (!isDraggingItem || !dragState) return;

    let animationFrameId: number | null = null;

    const handleMouseMove = (e: MouseEvent) => {
      if (animationFrameId !== null) return;

      animationFrameId = requestAnimationFrame(() => {
        animationFrameId = null;

        const deltaY = e.clientY - dragState.startY;

        const s = scaleRef.current;
        const rawMinutes = (deltaY / s / hourSlotHeight) * 60;
        const deltaMinutes =
          Math.round(rawMinutes / microSlotDuration) * microSlotDuration;
        const newPreviewTop =
          dragState.item.originalTop + (deltaMinutes / 60) * hourSlotHeight;

        if (newPreviewTop >= 0) {
          if (deltaMinutes !== 0) {
            setDidDrag(true);
          }

          setDragState((prev) =>
            prev
              ? {
                  ...prev,
                  currentDeltaMinutes: deltaMinutes,
                  previewTop: newPreviewTop,
                }
              : null
          );
        }
      });
    };

    const handleTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      if (animationFrameId !== null) return;

      animationFrameId = requestAnimationFrame(() => {
        animationFrameId = null;
        const touch = e.touches[0];

        const deltaY = touch.clientY - dragState.startY;

        const s = scaleRef.current;
        const rawMinutes = (deltaY / s / hourSlotHeight) * 60;
        const deltaMinutes =
          Math.round(rawMinutes / microSlotDuration) * microSlotDuration;
        const newPreviewTop =
          dragState.item.originalTop + (deltaMinutes / 60) * hourSlotHeight;

        if (newPreviewTop >= 0) {
          if (deltaMinutes !== 0) {
            setDidDrag(true);
          }

          setDragState((prev) =>
            prev
              ? {
                  ...prev,
                  currentDeltaMinutes: deltaMinutes,
                  previewTop: newPreviewTop,
                }
              : null
          );
        }
      });
    };

    const handleDragEnd = async () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }

      const { item, currentDeltaMinutes } = dragState;

      if (currentDeltaMinutes !== 0) {
        const dateStr = dateFns.format(new Date(date), DATE_FORMAT);

        const [startHours, startMinutes] = item.startedAt
          .split(':')
          .map(Number);
        const [endHours, endMinutes] = item.finishedAt.split(':').map(Number);

        const rawStartMinutes =
          startHours * 60 + startMinutes + currentDeltaMinutes;
        const rawEndMinutes = endHours * 60 + endMinutes + currentDeltaMinutes;

        const snappedStartMinutes =
          Math.round(rawStartMinutes / microSlotDuration) * microSlotDuration;
        const snappedEndMinutes =
          Math.round(rawEndMinutes / microSlotDuration) * microSlotDuration;

        const newStartHours = Math.floor(snappedStartMinutes / 60);
        const newStartMins = snappedStartMinutes % 60;
        const newEndHours = Math.floor(snappedEndMinutes / 60);
        const newEndMins = snappedEndMinutes % 60;

        const newStartedAt = `${String(newStartHours).padStart(2, '0')}:${String(newStartMins).padStart(2, '0')}`;
        const newFinishedAt = `${String(newEndHours).padStart(2, '0')}:${String(newEndMins).padStart(2, '0')}`;

        const newStartMin = timeToMinutes(newStartedAt);
        const newEndMin = timeToMinutes(newFinishedAt);

        const employeeData = calendarDataRef.current?.find(
          (e) => e.employee.id === item.employeeId
        );
        const workDay = employeeData?.workDay;

        if (
          workDay?.startedAt &&
          workDay?.finishedAt &&
          workDay.type !== 'dayOff'
        ) {
          const workStartMin = timeToMinutes(workDay.startedAt);
          const workEndMin = timeToMinutes(workDay.finishedAt);

          if (newStartMin < workStartMin || newEndMin > workEndMin) {
            setAlertMessage('Нельзя перемещать запись в нерабочее время');
            setDragState(null);
            setIsDraggingItem(false);
            setDidDrag(true);
            return;
          }
        }

        let overlapMessage = '';
        if (employeeData) {
          const overlappingItem = [
            ...employeeData.bookings.filter((b) => b.id !== item.id),
            ...employeeData.breaks.filter((b) => b.id !== item.id),
          ].find((other) => {
            const otherStart = timeToMinutes(other.startedAt);
            const otherEnd = timeToMinutes(other.finishedAt);
            return newStartMin < otherEnd && newEndMin > otherStart;
          });

          if (overlappingItem) {
            overlapMessage = `Новое время пересекается с другой записью или перерывом (${overlappingItem.startedAt}-${overlappingItem.finishedAt}). Всё равно перенести?`;
          }
        }

        const label = item.type === 'booking' ? 'запись' : 'перерыв';
        const message =
          overlapMessage ||
          `Перенести ${label} на ${newStartedAt}-${newFinishedAt}?`;

        setPendingMove({
          type: item.type,
          id: item.id,
          dateStr,
          newStartedAt,
          newFinishedAt,
          message,
        });
      }

      setDragState(null);
      setIsDraggingItem(false);
      setDidDrag(currentDeltaMinutes !== 0);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleDragEnd);
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleDragEnd);

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleDragEnd);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleDragEnd);
    };
  }, [isDraggingItem, dragState, hourSlotHeight, microSlotDuration, date]);

  useEffect(() => {
    return () => {
      clearDragTimeout();
      cancelDragListenersRef.current?.();
    };
  }, []);

  useEffect(() => {
    const container = calendarContainerRef.current;
    if (!container) return;

    const handlePinchStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        gestureState.current.initialDistance = getTouchDistance(e.touches);
        gestureState.current.initialScale = scaleRef.current;
        gestureState.current.isPinching = true;
      }
    };

    const handlePinchMove = (e: TouchEvent) => {
      // Only handle pinch gestures (2 fingers) and only when actively pinching
      if (e.touches.length === 2 && gestureState.current.isPinching) {
        e.preventDefault();
        const currentDistance = getTouchDistance(e.touches);
        const ratio = currentDistance / gestureState.current.initialDistance;
        const newScale = Math.min(
          2,
          Math.max(0.5, gestureState.current.initialScale * ratio)
        );
        scaleRef.current = newScale;
        syncTransforms();
        setScale(newScale);
      }
      // For single finger touches, do not prevent default to allow scrolling/dragging
    };

    const handlePinchEnd = (e: TouchEvent) => {
      if (e.touches.length < 2) {
        gestureState.current.isPinching = false;
        gestureState.current.initialDistance = 0;
        gestureState.current.initialScale = 1;
      }
    };

    container.addEventListener('touchstart', handlePinchStart, {
      passive: false,
    });
    window.addEventListener('touchmove', handlePinchMove, { passive: false });
    window.addEventListener('touchend', handlePinchEnd);

    return () => {
      container.removeEventListener('touchstart', handlePinchStart);
      window.removeEventListener('touchmove', handlePinchMove);
      window.removeEventListener('touchend', handlePinchEnd);
    };
  }, []);

  useLayoutEffect(() => {
    const el = calendarRef.current;
    if (!el) return;

    const measure = () => {
      setCalendarDimensions({
        width: el.scrollWidth,
        height: el.scrollHeight,
      });
    };

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(el);

    return () => observer.disconnect();
  }, [calendarData]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const container = calendarContainerRef.current;
    if (!container) return;
    const handleScroll = () => setScrollLeft(container.scrollLeft);
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isTimeInRange = (time: string) => {
    if (!calendarData) return true;
    const hasWorkingEmployees = calendarData.some(
      (e) => e.workDay && e.workDay?.type !== 'dayOff'
    );
    if (!hasWorkingEmployees) return true;
    return time >= minStartTime && time < maxEndTime;
  };

  useEffect(() => {
    if (pointId) {
      const dateStr = dateFns.format(date, DATE_FORMAT);
      bookingSocketService.connect(pointId, dateStr, handleDayUpdate);
    }

    return () => {
      bookingSocketService.disconnect();
    };
  }, [date, pointId]);

  const isEmployeeWorkingAt = (employee: IEmployeeDaySchedule, time: Date) => {
    if (!employee.workDay?.startedAt || !employee.workDay?.finishedAt) {
      return false;
    }
    if (employee.workDay.type === 'dayOff') {
      return false;
    }

    const timeMinutes = time.getHours() * 60 + time.getMinutes();
    const workStartMin = timeToMinutes(employee.workDay.startedAt);
    const workEndMin = timeToMinutes(employee.workDay.finishedAt);

    return timeMinutes >= workStartMin && timeMinutes < workEndMin;
  };

  const isTimelineVisible = useMemo(() => {
    if (!calendarData) return false;

    const currentMinutes =
      currentTime.getHours() * 60 + currentTime.getMinutes();
    const minStartMinutes = timeToMinutes(minStartTime);
    const maxEndMinutes = timeToMinutes(maxEndTime);

    if (currentMinutes < minStartMinutes || currentMinutes > maxEndMinutes) {
      return false;
    }

    return calendarData.some((e) => isEmployeeWorkingAt(e, currentTime));
  }, [calendarData, currentTime, minStartTime, maxEndTime]);

  const timelineWidth = useMemo(() => {
    const contentWidth = calendarDimensions?.width ?? 0;
    if (!contentWidth) return '100%';
    const s = scaleRef.current;
    const visualWidth = contentWidth * s;
    const visualTimeline = Math.min(visualWidth, viewportWidth);
    return `${visualTimeline / s}px`;
  }, [calendarDimensions, viewportWidth, scale]);

  const timelineLeft = useMemo(() => {
    const contentWidth = calendarDimensions?.width ?? 0;
    if (!contentWidth) return '0px';
    const s = scaleRef.current;
    const contentFitsViewport = contentWidth * s <= viewportWidth;
    if (contentFitsViewport) return '0px';
    return `${scrollLeft / s}px`;
  }, [calendarDimensions, viewportWidth, scrollLeft, scale]);

  const getSlotStartTime = (
    time: string,
    index: number,
    microSlotDuration: number
  ) => {
    const slotStartMinutes = timeToMinutes(time) + index * microSlotDuration;
    const slotStartHour = Math.floor(slotStartMinutes / 60);
    const slotStartMin = slotStartMinutes % 60;
    return `${String(slotStartHour).padStart(2, '0')}:${String(slotStartMin).padStart(2, '0')}`;
  };

  const syncTransforms = useCallback(() => {
    const s = scaleRef.current;
    const t = s !== 1 ? `scale(${s})` : 'none';
    const origin = 'top left';

    if (employeeHeaderRef.current) {
      employeeHeaderRef.current.style.flexShrink = '0';
      employeeHeaderRef.current.style.transformOrigin = origin;
      employeeHeaderRef.current.style.transform = t;
    }
    if (timesContainerRef.current) {
      timesContainerRef.current.style.transformOrigin = origin;
      timesContainerRef.current.style.transform = t;
    }
    if (calendarScalerRef.current) {
      calendarScalerRef.current.style.transformOrigin = origin;
      calendarScalerRef.current.style.transform = t;
      const timesWidth = timesContainerRef.current?.offsetWidth ?? 64;
      calendarScalerRef.current.style.marginLeft = `${timesWidth * (s - 1)}px`;
    }
    if (leftBorderRef.current && timesContainerRef.current) {
      leftBorderRef.current.style.width = `${timesContainerRef.current.offsetWidth * s}px`;
      leftBorderRef.current.style.borderRightWidth = `${2 * s}px`;
      leftBorderRef.current.style.borderBottomWidth = `${2 * s}px`;
      leftBorderRef.current.style.borderBottomStyle = 'solid';
      leftBorderRef.current.style.borderBottomColor = '#e3e4e8';
    }
    if (coreEmployeeRef.current) {
      coreEmployeeRef.current.style.height = `${50 * s}px`;
      coreEmployeeRef.current.style.borderBottomWidth = '0px';
    }
  }, []);

  useEffect(() => {
    syncTransforms();
  }, [scale, syncTransforms]);

  const handleDoubleClick = useCallback(() => {
    setScale(1);
    scaleRef.current = 1;
  }, []);

  const handleCloseAlert = useCallback(() => setAlertMessage(null), []);

  const getTouchDistance = (touches: TouchList): number => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  return {
    calendarData,
    setCalendarData,
    cardData,
    setCardData,
    dragState,
    setDragState,
    scale,
    setScale,
    isDraggingItem,
    setIsDraggingItem,
    didDrag,
    setDidDrag,
    selectedEmployeeId,
    setSelectedEmployeeId,
    createModalData,
    setCreateModalData,
    selectedBookingData,
    setSelectedBookingData,
    selectedBreakData,
    setSelectedBreakData,
    pendingMove,
    setPendingMove,
    alertMessage,
    setAlertMessage,
    getTimelinePosition,
    timelineWidth,
    timelineLeft,
    handleDoubleClick,
    calendarRef,
    calendarContainerRef,
    employeeListRef,
    employeeCardRefs,
    currentTime,
    hourSlotHeight,
    microSlotHeight: microSlotHeightBase,
    dayTimes,
    totalDayHeight,
    minStartTime,
    maxEndTime,
    getData,
    handleBlockClick,
    handleCloseCard,
    handleToggleEmployeePopup,
    handleCreateBooking,
    handleCreateBreak,
    handleConfirmMove,
    isTimeInRange,
    isTimelineVisible,
    toMinutes,
    getSlotStartTime,
    clearDragTimeout,
    cancelDragListenersRef,
    dragTimeoutRef,
    calendarDataRef,
    handleCloseAlert,
    pointId,
    modelDate: date,
    timeToMinutes,
    leftBorderRef,
    employeeHeaderRef,
    timesContainerRef,
    calendarScalerRef,
    calendarDimensions,
    timesWidth: timesContainerRef.current?.offsetWidth ?? 64,
    syncTransforms,
    coreEmployeeRef,
  };
}

export type DayCalendarModel = ReturnType<typeof useDayCalendarModel>;
